// Port of SuiteModel/Glossary.swift (review B26): a project's own words — an invented word, a name that isn't (yet) a
// character or place, a term of art — kept as one JSON entry per term in `settings.glossary`, keyed by an id every
// device derives from the term, so two devices adding different words merge and the same word twice doesn't duplicate.
// The glossary the writer sees is one list with the story's entities and the audiobook pronunciations.
//
// Suggestions need a spell checker, which the web platform doesn't expose (browsers check spelling only inside editable
// fields, with no API): `glossarySuggestions` takes it as a function, as the Swift does. The app could use a Hunspell
// dictionary in the project's language through `nspell` (or `hunspell-asm`/`typo-js`), e.g. `dictionary-en-gb`.
import type { EntityKind } from '../contract.ts';
import type { ProjectRecord, StoryEntity } from '../model/documents.ts';
import { swiftCompare } from '../model/order.ts';
import { stableUUID } from '../hashing.ts';
import { charIsLetter, charIsNumber, graphemes, isCFWhitespaceOrNewline, trimCFWhitespace } from '../unicode.ts';
import { charIsLowercase as isLowercase, charIsUppercase as isUppercase } from '../text/scalars.ts';
import { sentenceRanges } from '../text/sentences.ts';
import { entityNames, ENTITY_KIND_LABELS } from '../presence.ts';

export type GlossaryKind = 'word' | 'name' | 'place' | 'thing';
export const GLOSSARY_KINDS: readonly GlossaryKind[] = ['word', 'name', 'place', 'thing'];
export const GLOSSARY_KIND_LABELS: Record<GlossaryKind, string> = { word: 'Word', name: 'Name', place: 'Place', thing: 'Thing' };
/** The kind of entity @ completion makes for an entry (words are just words). */
export const GLOSSARY_ENTITY_KIND: Record<GlossaryKind, EntityKind | null> = { word: null, name: 'character', place: 'place', thing: 'object' };

export interface GlossaryEntry {
  id: string;
  term: string;
  kind: GlossaryKind;
  /** Other spellings the same entry covers. */
  variants: string[];
  note: string;
}

/** `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimAll(s: string): string {
  const cps = Array.from(s);
  let a = 0, b = cps.length;
  while (a < b && isCFWhitespaceOrNewline(cps[a].codePointAt(0)!)) a++;
  while (b > a && isCFWhitespaceOrNewline(cps[b - 1].codePointAt(0)!)) b--;
  return cps.slice(a, b).join('');
}

/** `GlossaryEntry.id(for:)`: the same term (ignoring case) always has the same id, on every device. */
export const glossaryID = (term: string) => stableUUID('glossary:' + trimAll(term).toLowerCase());

/** `GlossaryEntry(term:kind:variants:note:)`. */
export function glossaryEntry(term: string, kind: GlossaryKind = 'word', variants: string[] = [], note = ''): GlossaryEntry {
  const trimmed = trimAll(term);
  return { id: glossaryID(trimmed), term: trimmed, kind, variants, note };
}

/** `GlossaryEntry.spellings`: the term, then its variants (trimmed, empty ones left out). */
export const spellings = (e: GlossaryEntry) => [e.term, ...e.variants.map(trimCFWhitespace).filter((v) => v !== '')];

/** `GlossaryEntry(from:)`: throws where the Swift's decoder would (term missing or not text; id or note not text). */
export function decodeGlossaryEntry(json: unknown): GlossaryEntry {
  if (!json || typeof json !== 'object' || Array.isArray(json)) throw new Error('not an object');
  const o = json as Record<string, unknown>;
  if (typeof o.term !== 'string') throw new Error('term');
  if (o.id !== undefined && o.id !== null && typeof o.id !== 'string') throw new Error('id');
  if (o.note !== undefined && o.note !== null && typeof o.note !== 'string') throw new Error('note');
  return {
    id: typeof o.id === 'string' ? o.id : glossaryID(o.term),
    term: o.term,
    kind: (GLOSSARY_KINDS as readonly unknown[]).includes(o.kind) ? (o.kind as GlossaryKind) : 'word',
    variants: Array.isArray(o.variants) ? o.variants.filter((v): v is string => typeof v === 'string') : [],
    note: typeof o.note === 'string' ? o.note : '',
  };
}

const collator = new Intl.Collator('en', { sensitivity: 'accent' });
/** `localizedCaseInsensitiveCompare`. */
export const caseInsensitiveOrder = (a: string, b: string) => collator.compare(a, b);

/** `ProjectDocument.glossary()`: every readable entry, by term. */
export function readGlossary(project: ProjectRecord): GlossaryEntry[] {
  const map = project.settings.glossary;
  if (!map || typeof map !== 'object') return [];
  const out: GlossaryEntry[] = [];
  for (const json of Object.values(map as Record<string, unknown>)) {
    if (typeof json !== 'string' || json === '') continue;
    try { out.push(decodeGlossaryEntry(JSON.parse(json))); } catch { /* unreadable: left out */ }
  }
  return out.sort((a, b) => caseInsensitiveOrder(a.term, b.term));
}

/** `AudiobookSettings.pronunciations()`: written form → spoken form, as stored (one entry per word). */
export function readPronunciations(project: ProjectRecord): Record<string, string> {
  const map = project.settings.pronunciations;
  const out: Record<string, string> = {};
  if (!map || typeof map !== 'object') return out;
  for (const [k, v] of Object.entries(map as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
  return out;
}

export type GlossarySource = { type: 'entry'; id: string } | { type: 'entity'; id: string } | { type: 'pronunciation' };

/** `GlossaryRow`: one term the writer sees. */
export interface GlossaryRow {
  term: string;
  kindLabel: string;
  source: GlossarySource;
  sayAs: string | null;
  note: string;
  /** For an alias (or a variant): the entity's name (or the entry's term). */
  aliasOf: string | null;
  /** `term.lowercased()`. */
  id: string;
}

/** `Glossary.rows`: the project's own words, every entity (names and aliases), and any word with a pronunciation, each
 *  once (entities win, then entries), by term. */
export function glossaryRows(entries: readonly GlossaryEntry[], entities: readonly StoryEntity[], pronunciations: Record<string, string>): GlossaryRow[] {
  const rows = new Map<string, GlossaryRow>();
  const say = (term: string): string | null => {
    if (term in pronunciations) return pronunciations[term];
    const key = Object.keys(pronunciations).find((k) => k.toLowerCase() === term.toLowerCase());
    return key !== undefined ? pronunciations[key] : null;
  };
  for (const entity of entities) {
    const label = ENTITY_KIND_LABELS[entity.kind];
    entityNames(entity).forEach((name, i) => {
      if (trimCFWhitespace(name) === '') return;
      rows.set(name.toLowerCase(), {
        term: name, kindLabel: label, source: { type: 'entity', id: entity.id }, sayAs: say(name),
        note: i === 0 ? (entity.role ?? '') : '', aliasOf: i === 0 ? null : entity.name, id: name.toLowerCase(),
      });
    });
  }
  for (const entry of entries) {
    spellings(entry).forEach((spelling, i) => {
      if (rows.has(spelling.toLowerCase())) return;
      rows.set(spelling.toLowerCase(), {
        term: spelling, kindLabel: GLOSSARY_KIND_LABELS[entry.kind], source: { type: 'entry', id: entry.id }, sayAs: say(spelling),
        note: i === 0 ? entry.note : '', aliasOf: i === 0 ? null : entry.term, id: spelling.toLowerCase(),
      });
    });
  }
  for (const [written, spoken] of Object.entries(pronunciations)) {
    if (rows.has(written.toLowerCase())) continue;
    rows.set(written.toLowerCase(), { term: written, kindLabel: 'Word', source: { type: 'pronunciation' }, sayAs: spoken, note: '', aliasOf: null, id: written.toLowerCase() });
  }
  return [...rows.values()].sort((a, b) => caseInsensitiveOrder(a.term, b.term));
}

/** `Glossary.words(in:)`: letters with inner apostrophes; hyphens and spaces split. */
export function glossaryWords(text: string): string[] {
  const out: string[] = [];
  let current = '';
  const chars = graphemes(text);
  chars.forEach((c, i) => {
    if (charIsLetter(c) || charIsNumber(c)) {
      current += c;
    } else if ((c === "'" || c === '’') && current !== '' && i + 1 < chars.length && charIsLetter(chars[i + 1])) {
      current += c;
    } else if (current !== '') {
      out.push(current);
      current = '';
    }
  });
  if (current !== '') out.push(current);
  return out.filter((w) => graphemes(w).some(charIsLetter));
}

/** `Glossary.spellingWords`: the single words a spell checker should accept — every glossary spelling, entity name and
 *  alias, and the writer's own dictionary, split into words. */
export function spellingWords(entries: readonly GlossaryEntry[], entities: readonly StoryEntity[], dictionary: readonly string[] = []): Set<string> {
  const result = new Set<string>();
  for (const phrase of [...entries.flatMap(spellings), ...entities.flatMap(entityNames), ...dictionary]) {
    for (const word of glossaryWords(phrase)) result.add(word);
  }
  return result;
}

/** A word the spell checker doesn't know, how often it's used, and a sentence it's in. */
export interface GlossarySuggestion {
  word: string;
  count: number;
  example: string;
  /** Never written in lower case, and capitalised mid-sentence at least once: probably a name. */
  looksLikeName: boolean;
  id: string;
}

/** `Glossary.suggestions`: words in `texts` (plain text) a spell checker flags that aren't already known, used at least
 *  `minimum` times, most used first. Possessives count as the word ("Ilse's" → Ilse); words in capitals throughout and
 *  words with digits are left out. */
export function glossarySuggestions(texts: readonly string[], known: Iterable<string>, isMisspelled: (word: string) => boolean,
  options: { minimum?: number; limit?: number } = {}): GlossarySuggestion[] {
  const minimum = options.minimum ?? 2, limit = options.limit ?? 60;
  const knownLower = new Set([...known].map((k) => k.toLowerCase()));
  const counts = new Map<string, { count: number; capitalisedMidSentence: number; lowercase: number; spellings: Map<string, number>; example: string }>();
  for (const text of texts) {
    for (const [a, b] of sentenceRanges(text)) {
      const sentence = text.slice(a, b);
      glossaryWords(sentence).forEach((raw, i) => {
        let word = raw;
        for (const suffix of ["'s", '’s']) if (word.endsWith(suffix) && graphemes(word).length > 3) word = word.slice(0, -2);
        const chars = graphemes(word);
        if (!(chars.length > 1 && !chars.some(charIsNumber) && (word !== word.toUpperCase() || chars.length === 1))) return;
        const key = word.toLowerCase();
        if (knownLower.has(key)) return;
        let entry = counts.get(key);
        if (!entry) counts.set(key, (entry = { count: 0, capitalisedMidSentence: 0, lowercase: 0, spellings: new Map(), example: trimAll(sentence) }));
        entry.count += 1;
        if (i > 0 && isUppercase(chars[0])) entry.capitalisedMidSentence += 1;
        if (isLowercase(chars[0])) entry.lowercase += 1;
        entry.spellings.set(word, (entry.spellings.get(word) ?? 0) + 1);
      });
    }
  }
  // Check spelling once per distinct word, most used first, until there are enough.
  const result: GlossarySuggestion[] = [];
  const ordered = [...counts].filter(([, e]) => e.count >= minimum).sort((x, y) => y[1].count - x[1].count || swiftCompare(x[0], y[0]));
  for (const [, entry] of ordered) {
    let spelling = '', best = -1;
    for (const [s, n] of entry.spellings) if (n > best || (n === best && swiftCompare(s, spelling) < 0)) { spelling = s; best = n; }
    if (!isMisspelled(spelling)) continue;
    result.push({ word: spelling, count: entry.count, example: entry.example, looksLikeName: entry.lowercase === 0 && entry.capitalisedMidSentence > 0, id: spelling.toLowerCase() });
    if (result.length >= limit) break;
  }
  return result;
}

// MARK: The writer's dictionary (DictionarySync)

/** `DictionarySync`: the writer's own dictionary across projects, one key per word (`word.<lowercased>`) in a
 *  key-value store, so words added on two devices merge. */
export const DictionarySync = {
  prefix: 'word.',
  limit: 900,
  read(store: Record<string, unknown>): string[] {
    return Object.entries(store).filter(([k, v]) => k.startsWith(this.prefix) && typeof v === 'string').map(([, v]) => v as string).sort(caseInsensitiveOrder);
  },
  /** The keys to set and remove so the store holds exactly `words`. */
  write(words: readonly string[], store: Record<string, unknown>): { set: Record<string, string>; remove: string[] } {
    const wanted = new Map<string, string>();
    for (const word of words.slice(0, this.limit)) wanted.set(this.prefix + word.toLowerCase(), word);
    const remove = Object.keys(store).filter((k) => k.startsWith(this.prefix) && !wanted.has(k));
    const set: Record<string, string> = {};
    for (const [k, w] of wanted) if (store[k] !== w) set[k] = w;
    return { set, remove };
  },
  /** A device joining: the store's words and its own, together. */
  adopt(local: readonly string[], store: Record<string, unknown>): string[] {
    const merged = this.read(store);
    const have = new Set(merged.map((w) => w.toLowerCase()));
    merged.push(...local.filter((w) => !have.has(w.toLowerCase())));
    return merged.sort(caseInsensitiveOrder);
  },
};
